import { useTranslation } from 'react-i18next';
import { type ReactNode, useCallback, useState } from 'react';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components';
import ArrowLeftIcon from '@/shared/assets/arrow-left-icon.svg?react';
import { cn } from '@/shared/utils';
import {
  useElementHeight,
  useModalStore,
  useScrollDetection,
  useScrollOnFocus,
} from '@/shared/lib';
import { ConfirmationModal } from '@/widgets/confirmation-modal';

interface ModalComponentWrapperProps {
  title?: ReactNode | string;
  onBack?: () => void;
  description?: ReactNode;
  children: ReactNode;
  descriptionClassName?: string;
  actions?: ReactNode;
  customBackButton?: ReactNode;
  customCloseButton?: ReactNode;
  className?: string;
  headerClassName?: string;
  header?: ReactNode;
  footer?: ReactNode;
  footerClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
}
// to set header background color should used text color class
export function ModalComponentWrapper({
  title,
  onBack,
  description,
  children,
  descriptionClassName,
  actions,
  customBackButton,
  headerClassName,
  header,
  footer,
  className,
  footerClassName,
  contentClassName,
  titleClassName,
}: ModalComponentWrapperProps) {
  const { t } = useTranslation();
  const {
    dialogConfig,
    isCloseConfirmationRequired,
    isSkipBackButtonConfirmation,
  } = useModalStore();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const modalTitle = typeof title === 'string' ? t(title) : (title ?? '');
  const leftPadding = `sm:px-[3.2rem] px-[2.4rem]`;
  const { ref: headerRef, height: headerHeight } = useElementHeight();
  const { ref: contentRef, hasScrolled } = useScrollDetection();
  const { ref: footerRef, height: footerHeight } = useElementHeight();

  useScrollOnFocus({
    scrollContainerRef: contentRef as React.RefObject<HTMLElement>,
    enabled: true,
    offset: 20,
  });

  const handleBackClick = useCallback(() => {
    if (!onBack) return;

    const config = dialogConfig?.closeConfirmation;
    const shouldShowConfirmation =
      config && isCloseConfirmationRequired && !isSkipBackButtonConfirmation;

    if (shouldShowConfirmation) {
      setIsConfirmationOpen(true);
    } else {
      onBack();
    }
  }, [
    onBack,
    dialogConfig,
    isCloseConfirmationRequired,
    isSkipBackButtonConfirmation,
  ]);

  const handleConfirmationConfirm = useCallback(() => {
    setIsConfirmationOpen(false);
    onBack?.();
  }, [onBack]);

  const handleConfirmationCancel = useCallback(() => {
    setIsConfirmationOpen(false);
  }, []);

  const contentStyles = {
    '--header-height': `${headerHeight}px`,
    marginTop: `-${headerHeight}px`,
    paddingTop: `${headerHeight}px`,
    paddingBottom: footer && footerHeight ? `${footerHeight}px` : '',
  };

  const confirmationConfig = dialogConfig?.closeConfirmation;

  return (
    <>
      <div
        className={cn(
          'w-full flex flex-col min-h-0 max-h-full overflow-hidden rounded-[2.4rem]',
          className
        )}>
        <DialogHeader
          ref={headerRef}
          className={cn(
            `text-left shrink-0 sm:pt-[3.2rem] pt-[2.4rem] pb-[0.8rem] absolute w-full backdrop-blur-[8px] bg-background/50 border-b border-transparent z-10 text-light`,
            'before:block before:absolute before:left-0 before:right-0 before:top-0 before:h-full before:bg-[linear-gradient(currentColor_0%,currentColor_80%,transparent_100%)]',
            leftPadding,
            hasScrolled ? 'border-border' : '',
            headerClassName
          )}>
          <div
            className={cn(
              'relative flex w-full',
              actions ? 'pr-[4.2rem]' : ''
            )}>
            {customBackButton ||
              (onBack && (
                <Button
                  variant={'secondary'}
                  onClick={handleBackClick}
                  className="flex items-center justify-start bg-transparent-bg hover:bg-transparent-bg transition-colors text-foreground shadow-none p-0 w-[4.2rem] h-[4.2rem]"
                  aria-label={t('common.back')}>
                  <ArrowLeftIcon className="w-[2.4rem] h-[2.4rem]" />
                </Button>
              ))}
            {modalTitle && (
              <DialogTitle
                className={cn(
                  'headline1 text-foreground py-[0.5rem] flex-1',
                  !actions && 'me-[5rem]',
                  titleClassName
                )}>
                {modalTitle}
              </DialogTitle>
            )}
            {actions && (
              <div className="flex items-center gap-[1.6rem]">{actions}</div>
            )}
          </div>
        </DialogHeader>

        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={cn(
            ` flex-1 min-h-0 overflow-y-auto sm:gap-[3.2rem] gap-[2.4em] flex flex-col sm:pb-[3.2rem] pb-[2.4rem]`,
            'before:block before:min-h-[var(--header-height)]',
            leftPadding,
            contentClassName,
            description
              ? 'sm:before:mb-[-2.6rem] before:mb-[-1.8rem]'
              : 'sm:pt-[2.6rem] pt-[1.8rem] before:mb-[-0.8rem]' // + 0.8rem from header
          )}
          style={contentStyles}>
          {description && (
            <DialogDescription
              className={cn(
                'p3 text-muted-foreground text-left',
                descriptionClassName
              )}>
              {description}
            </DialogDescription>
          )}

          {header}
          {children}
        </div>

        {footer && (
          <div
            ref={footerRef}
            className={cn(
              'w-full sm:py-[3.2rem] py-[2.4rem] absolute bottom-0 before:block z-0 text-light',
              'before:absolute before:top-[1px] sm:before:h-[3.2rem] before:h-[2.4rem] before:left-0 before:right-0 before:bg-[linear-gradient(transparent,currentColor_100%)] before:pointer-events-none',
              'after:block after:absolute after:bottom-0 after:left-0 after:right-0 sm:after:h-[calc(100%-3.2rem)] after:h-[calc(100%-2.4rem)] after:bg-background after:z-[-1]',
              leftPadding,
              footerClassName
            )}>
            {footer}
          </div>
        )}
      </div>

      {confirmationConfig && (
        <ConfirmationModal
          isOpen={isConfirmationOpen}
          onClose={handleConfirmationCancel}
          onConfirm={handleConfirmationConfirm}
          title={confirmationConfig.title}
          description={confirmationConfig.description}
          confirmText={confirmationConfig.confirmText}
          cancelText={confirmationConfig.cancelText}
          confirmVariant={confirmationConfig.confirmVariant}
          showCancelButton={confirmationConfig.showCancelButton}
        />
      )}
    </>
  );
}
